import T from 'i18n-react';
import React, { Component } from 'react';
import InputMask from 'react-input-mask';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LogLevel } from '../../../constants/LogLevelConstants';
import { PaymentVendorProcessType } from '../../../constants/PaymentVendorProcessTypeConstants';
import * as CommonStore from '../../../store/Common';

/**
 * Allowlist of hostnames from which the Forte SDK script may be loaded.
 * Only HTTPS origins are accepted — protocol is enforced inside isForteUrlAllowed.
 */
const FORTE_ALLOWED_HOSTS = ['api.forte.net', 'sandbox.forte.net'];

/**
 * Returns true only when `url` is a valid HTTPS URL whose hostname
 * is exactly one of the entries in FORTE_ALLOWED_HOSTS (or a direct
 * subdomain of one, e.g. js.sandbox.forte.net).
 */
function isForteUrlAllowed(url) {
    try {
        const { protocol, hostname } = new URL(url);
        return (
            protocol === 'https:' &&
            FORTE_ALLOWED_HOSTS.some(
                host => hostname === host || hostname.endsWith('.' + host)
            )
        );
    } catch {
        return false;
    }
}

class ForteForm extends Component {
    constructor(props) {
        super(props);

        this.cardNumberValueRef = React.createRef();
        this.expirationRef = React.createRef();
        this.cvvRef = React.createRef();

        this.state = {
            sdkLoaded: false,
            // SGCIS-11192: blocks a second click while tokenization is in flight. Without it a double
            // click produced two tokens, two charges, and two posts on the same paymentAttemptID.
            isSubmitting: false,
            apiLoginId: '',
            sdkUrl: '',
            cardAccountName: '',
            bankAccountName: '',
            cardholderName: '',
            nameOnAccount: '',
            routingNumber: '',
            accountNumber: '',
            accountType: 'c',
            oneTimeTokenDev: '',
            fields: [
                {
                    name: 'cardAccountName',
                    isRequired: true,
                    forPaymentType: 'CreditCard'
                },
                {
                    name: 'cardholderName',
                    isRequired: true,
                    forPaymentType: 'CreditCard',
                    minTextWords: 2,
                    onValidating: CommonStore.hasMinWordsAmount
                },
                {
                    name: 'cardNumberValue',
                    isRequired: true,
                    forPaymentType: 'CreditCard',
                    onValidating: CommonStore.isValidCreditCardNumber,
                    isDomValue: true
                },
                {
                    name: 'expiration',
                    isRequired: true,
                    forPaymentType: 'CreditCard',
                    onValidating: CommonStore.isValidCreditCardExpDate,
                    isDomValue: true
                },
                {
                    name: 'cvv',
                    isRequired: true,
                    forPaymentType: 'CreditCard',
                    onValidating: CommonStore.isValidCvv,
                    isDomValue: true
                },
                {
                    name: 'bankAccountName',
                    isRequired: true,
                    forPaymentType: 'ECheck'
                },
                {
                    name: 'nameOnAccount',
                    isRequired: true,
                    forPaymentType: 'ECheck',
                    minTextWords: 2,
                    onValidating: CommonStore.hasMinWordsAmount
                },
                {
                    name: 'routingNumber',
                    isRequired: true,
                    forPaymentType: 'ECheck'
                },
                {
                    name: 'accountNumber',
                    isRequired: true,
                    forPaymentType: 'ECheck'
                }
            ]
        };

        this.handleLoad = this.handleLoad.bind(this);
    }

    componentDidMount() {
        // Hide fields already handled by this form in the parent layout
        if (this.props.onHideRecessiveFields) {
            this.props.onHideRecessiveFields([
                'cardAccountName',
                'bankAccountName',
                'bankNameOnAccount',
                'cardNameOnAccount'
            ]);
        }

        this.initializeConfig();
    }

    initializeConfig = () => {
        try {
            const { content } = this.props;
            const configB64 = CommonStore.decompressString(content);
            const forteConfig = JSON.parse(configB64 || '{}');

            if (forteConfig.ApiLoginID && forteConfig.Url) {
                this.setState(
                    { apiLoginId: forteConfig.ApiLoginID, sdkUrl: forteConfig.Url },
                    this.loadForteSDK
                );
            } else {
                if (this.props.setPaymentError) {
                    const msg = T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.';
                    this.props.setPaymentError(msg);
                }
                this.handleLoad();
            }
        } catch (error) {
            const { sourceAccount, sourceName, postServerLog } = this.props;
            const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;

            if (postServerLog) {
                postServerLog(
                    LogLevel.Error,
                    sourceName
                    + " (operation: initializeConfig, AccountID = " + accountID + "): "
                    + "Forte configuration parse/decompress failure."
                    + (error ? " Error: " + error : "")
                );
            }

            if (this.props.setPaymentError) {
                const msg = T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.';
                this.props.setPaymentError(msg);
            }

            this.handleLoad();
        }
    }

    loadForteSDK = () => {
        const { sdkUrl } = this.state;

        if (!isForteUrlAllowed(sdkUrl)) {
            const { sourceAccount, sourceName, postServerLog, setPaymentError } = this.props;
            const accountID = sourceAccount ? (sourceAccount.accountID || 0) : 0;

            if (postServerLog) {
                postServerLog(
                    LogLevel.Error,
                    sourceName
                    + ' (operation: loadForteSDK, AccountID = ' + accountID + '): '
                    + 'Forte SDK URL rejected — not in allowlist: ' + sdkUrl
                );
            }

            if (setPaymentError) {
                const msg = T.translate('accountPay.paymentConfigurationError') || 'Failed to load payment configuration.';
                setPaymentError(msg);
            }

            this.handleLoad();
            return;
        }

        if (document.getElementById('forteSdk')) {
            this.setState({ sdkLoaded: true }, this.handleLoad);
            return;
        }

        const script = document.createElement('script');
        script.id = 'forteSdk';
        script.src = sdkUrl;
        script.async = true;
        script.onload = () => this.setState({ sdkLoaded: true }, this.handleLoad);
        script.onerror = () => {
            if (this.props.setPaymentError) {
                this.props.setPaymentError(T.translate('accountPay.iframeLoadError') || 'Failed to load payment SDK.');
            }

            this.setState({ sdkLoaded: true }, this.handleLoad);
        };
        document.head.appendChild(script);
    }

    onChange = (e) => {
        const { name, value } = e.target;
        const fields = [...this.state.fields];
        const fieldIdx = fields.findIndex(f => f.name === name);

        if (fieldIdx !== -1) {
            const field = { ...fields[fieldIdx] };

            // Clear validation error as the user types
            if (field.isRequired && field.isRequiredError && value) {
                if (field.onValidating) {
                    field.onValidating(field, e, this.props);
                } else {
                    field.isRequiredError = false;
                }
            }

            fields[fieldIdx] = field;
            this.setState({ fields });

            // isDomValue fields (card number, expiration, cvv) are managed by refs — skip state
            if (field.isDomValue) {
                return;
            }
        }

        this.setState({ [name]: value });
    }

    isFormValid = () => {
        const { paymentMethod, paymentVendorProcessTypeID } = this.props;
        const isAutoPayment = paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment;
        const nicknameFields = ['cardAccountName', 'bankAccountName'];
        let valid = true;

        const fields = this.state.fields.map((field) => {
            // Only validate fields that belong to the active payment method
            if (field.forPaymentType && field.forPaymentType !== paymentMethod) {
                return field;
            }

            // Clone before mutating — never write directly to state object references
            const nextField = { ...field };

            // Read value from DOM ref or state
            if (nextField.isDomValue) {
                nextField.value = this[nextField.name + 'Ref'].current
                    ? this[nextField.name + 'Ref'].current.value
                    : '';
            } else {
                nextField.value = this.state[nextField.name];
            }

            nextField.isRequiredError = false;

            // Nickname fields are only required for AutoPayment
            const effectiveIsRequired = nicknameFields.includes(nextField.name)
                ? isAutoPayment
                : nextField.isRequired;

            if (effectiveIsRequired && !nextField.value) {
                nextField.isRequiredError = true;
                valid = false;
            }

            if (nextField.onValidating && nextField.value) {
                valid = nextField.onValidating(nextField, undefined, this.props) && valid;
            }

            // Do not persist DOM values in state
            if (nextField.isDomValue) {
                delete nextField.value;
            }

            return nextField;
        });

        this.setState({ fields });

        if (valid) {
            if (this.props.setPaymentError) this.props.setPaymentError('');
        } else {
            if (this.props.setPaymentError) this.props.setPaymentError(T.translate('common.thereAreMissingFields'));
        }

        return valid;
    }

    onPayClick = (e) => {
        e.preventDefault();

        // SGCIS-11192: one click, one tokenization, one payment.
        if (this.state.isSubmitting) return;

        if (!this.isFormValid()) return;

        this.setState({ isSubmitting: true });

        const { oneTimeTokenDev } = this.state;
        const isLocal = window.location.hostname === 'localhost';

        // Dev mode: bypass real tokenization
        if (isLocal) {
            if (oneTimeTokenDev) {
                this.handleSuccess({
                    event: 'success',
                    onetime_token: oneTimeTokenDev,
                    response_description: 'APPROVED',
                    last_4: '1111',
                    card_type: 'visa',
                    expire_month: "01",
                    expire_year: "2030",
                    cardholderName: 'TEST CARD',
                    nameOnAccount: 'TEST ACH'
                });
            } else {
                this.handleError({ response_description: "Missing dev mode token" })
            }

            return;
        }

        if (window.forte && typeof window.forte.createToken === 'function') {
            window.forte.createToken(this.getForteData())
                .success(this.handleSuccess)
                .error(this.handleError);
        } else {
            // Forte SDK is not available; surface an explicit error to the user
            this.handleError({
                response_description: 'Payment service is currently unavailable. Please try again later.'
            });
        }
    }

    // Reads ref values and maps to the Forte SDK payload shape
    getForteData = () => {
        const { paymentMethod } = this.props;
        const base = { api_login_id: this.state.apiLoginId };

        if (paymentMethod === 'CreditCard') {
            const cardNumber = this.cardNumberValueRef.current
                ? this.cardNumberValueRef.current.value.replace(/[\s_-]/g, '')
                : '';
            const expiration = this.expirationRef.current ? this.expirationRef.current.value : '';
            const cvv = this.cvvRef.current ? this.cvvRef.current.value : '';

            // Parse MM/YY → 4-digit year expected by Forte
            const [expireMonth = '', expireYearShort = ''] = expiration.split('/');
            const expireYear = expireYearShort.trim() ? `20${expireYearShort.trim()}` : '';

            return { ...base, card_number: cardNumber, expire_month: expireMonth.trim(), expire_year: expireYear, cvv };
        }

        return {
            ...base,
            routing_number: this.state.routingNumber,
            account_number: this.state.accountNumber,
            account_type: this.state.accountType
        };
    }

    handleSuccess = (response) => {
        if (!response) return;

        const { onPaymentSuccess, onPaymentMethodSuccess, paymentVendorProcessTypeID, paymentMethod } = this.props;
        const isCreditCard = paymentMethod === 'CreditCard';
        const holderName = isCreditCard ? this.state.cardholderName : this.state.nameOnAccount;

        const normalizedResponse = {
            ...response,
            vendor: 'Forte',
            token: response.onetime_token,
            nameOnAccount: holderName,
            operation: isCreditCard ? 'SaveCCToken' : 'SaveACHToken'
        };

        const buildPaymentData = (includeNickname = false) => {
            const baseData = {
                ...normalizedResponse,
                holderName: isCreditCard ? (response.cardholderName || holderName) : (response.nameOnAccount || holderName)
            };

            if (includeNickname) {
                const { cardAccountName, bankAccountName } = this.state;
                baseData.nickname = isCreditCard ? cardAccountName : bankAccountName;
            }

            if (isCreditCard) {
                baseData.cardType = response.card_type;
                baseData.cardNumber = '************' + (response.last_4 || '');
            }
            else {
                baseData.accountNumber = '************' + (response.last_4 || '');
            }

            return baseData;
        };

        if (paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment && onPaymentMethodSuccess) {
            onPaymentMethodSuccess(buildPaymentData(true));
        } else if (onPaymentSuccess) {
            onPaymentSuccess(buildPaymentData(false));
        }
    }

    handleError = (error) => {
        const msg = error.response_description
            || T.translate('accountPay.paymentProcessingError')
            || 'Tokenization failed.';
        if (this.props.setPaymentError) this.props.setPaymentError(msg);

        // Nothing was charged and the PaymentAttempt is untouched, so let the customer correct the
        // card details and try again.
        this.setState({ isSubmitting: false });
    }

    handleLoad() {
        if (this.props.onLoaded) {
            this.props.onLoaded();
        }
    }

    // Dynamic card mask based on card number prefix (mirrors PayCreditCard.js)
    getCreditCardMask = () => {
        const cardNumber = this.cardNumberValueRef.current ? this.cardNumberValueRef.current.value : '';
        return CommonStore.getCardInfo(cardNumber, false).mask;
    }

    getFieldByName = (name) => this.state.fields.find(f => f.name === name) || {};

    renderDevMode() {
        return (
            <div className="form-group">
                <label className="small">One Time Token (Dev Mode)</label>
                <input className="form-control" onChange={e => this.setState({ oneTimeTokenDev: e.target.value })} />
            </div>
        );
    }

    renderCreditCard() {
        const { cardholderName } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        const ccNicknameField = this.getFieldByName('cardAccountName');
        const ccNameField = this.getFieldByName('cardholderName');
        const cardNumberField = this.getFieldByName('cardNumberValue');
        const expirationField = this.getFieldByName('expiration');
        const cvvField = this.getFieldByName('cvv');

        return (
            <div className="forte-credit-card">

                {/* Card Nickname field inside the credit card form */}
                {paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment &&
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="cardAccountName">{T.translate('paymentMethod.cardNickname')}</label>
                                <input
                                    type="text"
                                    id="cardAccountName"
                                    name="cardAccountName"
                                    value={this.state.cardAccountName}
                                    onChange={this.onChange}
                                    className={'form-control ' + (ccNicknameField.isRequiredError ? 'is-invalid' : '')}
                                    autoComplete="off"
                                />
                                <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                                <span className="input-details">{T.translate('paymentMethod.giveThisCardNickname')}</span>
                            </div>
                        </div>
                    </div>
                }

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="cardholderName">{T.translate('accountPay.nameOnCard')}</label>
                            <input
                                type="text"
                                id="cardholderName"
                                name="cardholderName"
                                value={cardholderName}
                                onChange={this.onChange}
                                className={'form-control ' + (ccNameField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('accountPay.holderNameRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group credit-card-input">
                            <label htmlFor="cardNumberValue">{T.translate('accountPay.cardNumber')}</label>
                            <InputMask
                                ref={this.cardNumberValueRef}
                                mask={this.getCreditCardMask()}
                                placeholder="#### #### #### ####"
                                id="cardNumberValue"
                                name="cardNumberValue"
                                onChange={this.onChange}
                                className={'form-control ' + (cardNumberField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="expiration">{T.translate('accountPay.expiration')}</label>
                            <InputMask
                                ref={this.expirationRef}
                                mask="99/99"
                                placeholder="MM/YY"
                                id="expiration"
                                name="expiration"
                                onChange={this.onChange}
                                className={'form-control ' + (expirationField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="form-group">
                            <label htmlFor="cvv">{T.translate('accountPay.securityCode')}</label>
                            <input
                                type="number"
                                ref={this.cvvRef}
                                id="cvv"
                                name="cvv"
                                maxLength={4}
                                onChange={this.onChange}
                                className={'form-control ' + (cvvField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderAch() {
        const { accountType, nameOnAccount, routingNumber, accountNumber } = this.state;
        const { paymentVendorProcessTypeID } = this.props;

        const ccNicknameField = this.getFieldByName('bankAccountName');
        const achNameField = this.getFieldByName('nameOnAccount');
        const routingField = this.getFieldByName('routingNumber');
        const accountNumberField = this.getFieldByName('accountNumber');

        return (
            <div className="forte-ach">

                {/* Bank Account Nickname field inside the bank account form */}
                {paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment &&
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group">
                                <label htmlFor="bankAccountName">{T.translate('paymentMethod.bankAccountNickname')}</label>
                                <input
                                    type="text"
                                    id="bankAccountName"
                                    name="bankAccountName"
                                    value={this.state.bankAccountName}
                                    onChange={this.onChange}
                                    className={"form-control " + (ccNicknameField.isRequiredError ? 'is-invalid' : '')}
                                    autoComplete="off"
                                />
                                <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                                <span className="input-details">{T.translate('paymentMethod.giveThisBankAccountNickname')}</span>
                            </div>
                        </div>
                    </div>
                }

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="nameOnAccount">{T.translate('autopay.accountHolderName')}</label>
                            <input
                                type="text"
                                id="nameOnAccount"
                                name="nameOnAccount"
                                value={nameOnAccount}
                                onChange={this.onChange}
                                className={'form-control ' + (achNameField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('accountPay.holderNameRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="bankAccountType">{T.translate('autopay.bankAccountType')}</label>
                            <select
                                className="form-control"
                                id="bankAccountType"
                                name="accountType"
                                value={accountType}
                                onChange={this.onChange}
                            >
                                <option value="c">{T.translate('payment.bankTypes.checking') || 'Checking'}</option>
                                <option value="s">{T.translate('payment.bankTypes.savings') || 'Savings'}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="routingNumber">{T.translate('accountPay.routingNumber')}</label>
                            <input
                                id="routingNumber"
                                name="routingNumber"
                                maxLength={9}
                                value={routingNumber}
                                onChange={this.onChange}
                                className={'form-control ' + (routingField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="accountNumber">{T.translate('accountPay.bankAccountNumber')}</label>
                            <input
                                type="text"
                                id="accountNumber"
                                name="accountNumber"
                                maxLength={17}
                                value={accountNumber}
                                onChange={this.onChange}
                                className={'form-control ' + (accountNumberField.isRequiredError ? 'is-invalid' : '')}
                                autoComplete="off"
                            />
                            <div className="invalid-feedback">{T.translate('common.thisFieldIsRequired')}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { sdkLoaded } = this.state;
        const { paymentMethod, paymentVendorProcessTypeID } = this.props;
        const isAutoPayment = paymentVendorProcessTypeID === PaymentVendorProcessType.AutoPayment;
        const isLocal = window.location.hostname === 'localhost';

        if (!sdkLoaded) return null;

        const paymentMethodText = paymentMethod ? paymentMethod.toLowerCase() : paymentMethod;

        return (
            <div className="payment-component forte-container">

                {isLocal && this.renderDevMode()}
                {paymentMethodText === 'creditcard' && this.renderCreditCard()}
                {paymentMethodText === 'echeck' && this.renderAch()}

                <div className="text-right mt-3">
                    <button className="btn btn-primary" onClick={this.onPayClick} disabled={this.state.isSubmitting} title="Forte Payment" >
                        {isAutoPayment ? T.translate('paymentMethod.savePaymentMethod') : T.translate('accountPay.submitPayment')}
                    </button>
                </div>
            </div>
        );
    }
}

export default connect(
    state => ({ ...state.common }),
    dispatch => bindActionCreators({ ...CommonStore.actionCreators }, dispatch)
)(ForteForm);